import React, {type Dispatch, type SetStateAction, useCallback,
    useMemo,
    useState,
} from 'react';

import debounce from 'lodash.debounce';
import {SectionFilter} from 'src/components/section-filter';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {WidthItem} from 'src/components/width-item';
import {SortDirection as SortDirections} from 'src/openapi/admin/models/SortDirection';

import {FlatButton} from '@pb/kit-component-button';
import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import FormSection from '@pb/kit-component-form-section';
import {Group, Item} from '@pb/kit-component-group';
import {Icons} from '@pb/kit-component-icon';
import {BasicIconButton} from '@pb/kit-component-icon-button';
import {PaginationStripe} from '@pb/kit-component-pagination';
import {Body, Cell, Head, Row, Table} from '@pb/kit-component-table';

import {AddProjectCodeDialog} from './add-project-code-dialog';
import {type ProjectCodesFilters, SECTION_NAME} from './constants';
import {ProjectCodesFiltersBody} from './project-codes-filters-body';
import {ProjectCodesSkeleton} from './project-codes.skeleton';

import type {Error} from 'src/openapi/admin/models/Error';
import type {UserProjectCode} from 'src/openapi/admin/models/UserProjectCode';

type Props = {
    isLoading: boolean,
    loadingError: Error | null,
    projectCodes: UserProjectCode[],
    setProjectCodes: Dispatch<SetStateAction<UserProjectCode[]>>,
    disabled?: boolean,
    onFieldChange: (parameterName: string) => void,
};

export const ProjectCodes = (props: Props) => {
    const {
        isLoading,
        loadingError,
        projectCodes,
        setProjectCodes,
        disabled,
        onFieldChange,
    } = props;

    const [filters, setFilters] = useState<ProjectCodesFilters>({});
    const [{currentPage, pageSize}, setPagination] = useState({currentPage: 1, pageSize: 5});
    const [sortDirection, setSortDirection] = useState<SortDirections>(SortDirections.ASC);

    const changeSortDirection = useCallback(() => {
        setSortDirection(prevState => (prevState === SortDirections.ASC
            ? SortDirections.DESC
            : SortDirections.ASC
        ));
    }, [setSortDirection]);

    const hideProjectCode = useCallback((id: number) => {
        setProjectCodes(prevState => prevState.map(projectCode => {
            if (projectCode.codeId === id) {
                return {...projectCode, active: false};
            }
            return projectCode;
        }));
        onFieldChange('Hide Project Codes');
    }, [setProjectCodes, onFieldChange]);

    const restoreProjectCode = useCallback((id: number) => {
        setProjectCodes(prevState => prevState.map(projectCode => {
            if (projectCode.codeId === id) {
                return {...projectCode, active: true};
            }
            return projectCode;
        }));
        onFieldChange('Restore Project Codes');
    }, [setProjectCodes, onFieldChange]);

    const filteredCodes = useMemo(() => projectCodes.filter(({description, active}) =>
        (!filters.name || description.includes(filters.name)) &&
        (!filters.activeOnly || active),
    ), [filters, projectCodes]);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const createProjectCode = useCallback(debounce((code: string) => {
        setProjectCodes(prevState => [...prevState, {
            codeId: prevState.length + 1,
            description: code,
            active: true,
        }]);

        onFieldChange('Create Project Codes');
    }, 500), [setProjectCodes, onFieldChange]);

    const applyFilters = useCallback((filter: ProjectCodesFilters) => {
        setPagination({currentPage: 1, pageSize});
        setFilters(filter);
    }, [pageSize, setFilters, setPagination]);

    const clearFilters = useCallback(() => {
        setPagination({currentPage: 1, pageSize});
        setFilters({});

        return {};
    }, [setFilters, setPagination, pageSize]);

    return (
        <Skeleton shown={isLoading} view={<ProjectCodesSkeleton/>}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}
                sectionName={SECTION_NAME}>
                <FormSection headline={SECTION_NAME}>
                    <WidthItem width={624}>
                        <Group justify="end">
                            <AddProjectCodeDialog
                                codes={projectCodes}
                                onCreate={createProjectCode}
                                disabled={disabled}
                            />
                            <SectionFilter<ProjectCodesFilters>
                                onApply={applyFilters}
                                onClear={clearFilters}
                                filters={filters}
                                defaultFilters={{}}>
                                {(filter, onChange) => (
                                    <ProjectCodesFiltersBody filter={filter} onChange={onChange}/>
                                )}
                            </SectionFilter>
                        </Group>
                        <Group direction="v">
                            <Table borderedGrid={true}>
                                <Head>
                                    <Cell width={450}>
                                        <Group justify="space-between">
                                            <Item>Project Code</Item>
                                            <Item>
                                                <BasicIconButton
                                                    iconProps={{type: sortDirection === SortDirections.DESC
                                                        ? Icons.LONG_ARROW_DOWN
                                                        : Icons.LONG_ARROW_UP,
                                                    }}
                                                    onClick={changeSortDirection}
                                                />
                                            </Item>
                                        </Group>
                                    </Cell>
                                    <Cell>Actions</Cell>
                                </Head>
                                <Body>
                                    {filteredCodes
                                        .sort((a, b) => {
                                            if (sortDirection === SortDirections.ASC) {
                                                return a.description.localeCompare(b.description);
                                            }
                                            return b.description.localeCompare(a.description);
                                        })
                                        .slice((currentPage - 1) * pageSize, currentPage * pageSize)
                                        .map(({codeId, description, active}) => (
                                            <Row key={codeId}>
                                                <Cell>
                                                    <EllipsisTextHint title={description}>
                                                        <span className={!active ? 'disabled' : undefined}>
                                                            {description}
                                                        </span>
                                                    </EllipsisTextHint>
                                                </Cell>
                                                <Cell>{active ? (
                                                    <FlatButton
                                                        disabled={disabled}
                                                        onClick={() => hideProjectCode(codeId)}>
                                                        Hide
                                                    </FlatButton>
                                                ) : (
                                                    <FlatButton
                                                        disabled={disabled}
                                                        onClick={() => restoreProjectCode(codeId)}>
                                                        Restore
                                                    </FlatButton>
                                                )}
                                                </Cell>
                                            </Row>
                                        ))}
                                    {!filteredCodes.length && (
                                        <Row>
                                            <Cell colSpan={2} className="text-align_center">
                                                No results have been found
                                            </Cell>
                                        </Row>
                                    )}
                                </Body>
                            </Table>
                            <PaginationStripe
                                totalCount={filteredCodes.length}
                                currentPage={currentPage}
                                pageSize={pageSize}
                                onPaginationChange={setPagination}
                                sizes={[5, 10]}
                            />
                        </Group>
                    </WidthItem>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
